import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Typography, Button, Spin } from 'antd';
import { AppleOutlined } from '@ant-design/icons';
import { inviteCopyEn } from '../../content/invite.en';
import { fetchInviteDetails, type InviteDetails } from '../../services/inviteService';
import styles from './Invite.module.css';

const { Title, Paragraph } = Typography;

const APP_STORE_APP_ID = import.meta.env.VITE_APP_STORE_APP_ID || '6757135655';
const APP_STORE_URL =
  import.meta.env.VITE_APP_STORE_URL || `https://apps.apple.com/app/id${APP_STORE_APP_ID}`;

type PageState =
  | { kind: 'loading' }
  | { kind: 'ready'; invite: InviteDetails | null; message?: string }
  | { kind: 'expired' }
  | { kind: 'not_found' };

const Invite: React.FC = () => {
  const { inviteId, token } = useParams<{ inviteId?: string; token?: string }>();
  const qrToken = token?.trim() || undefined;
  const deepLinkPath = inviteId
    ? `/invite/${encodeURIComponent(inviteId)}`
    : qrToken
      ? `/qr/${encodeURIComponent(qrToken)}`
      : null;
  const [pageState, setPageState] = useState<PageState>({ kind: 'loading' });

  useEffect(() => {
    if (!inviteId && !qrToken) {
      setPageState({ kind: 'not_found' });
      return;
    }

    // Camera QR tokens are not connection-invite ids; show App Store landing without lookup.
    if (!inviteId) {
      setPageState({ kind: 'ready', invite: null });
      return;
    }

    let cancelled = false;

    (async () => {
      const result = await fetchInviteDetails(inviteId);
      if (cancelled) return;

      if (result.ok) {
        setPageState({ kind: 'ready', invite: result.invite });
        return;
      }

      if (result.reason === 'expired') {
        setPageState({ kind: 'expired' });
        return;
      }

      if (result.reason === 'not_found') {
        setPageState({ kind: 'not_found' });
        return;
      }

      setPageState({
        kind: 'ready',
        invite: null,
        message: inviteCopyEn.fetchError,
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [inviteId, qrToken]);

  useEffect(() => {
    if (!deepLinkPath || typeof document === 'undefined') return;

    const inviteUrl = `${window.location.origin}${deepLinkPath}`;
    const meta = document.createElement('meta');
    meta.name = 'apple-itunes-app';
    meta.content = `app-id=${APP_STORE_APP_ID}, app-argument=${inviteUrl}`;
    document.head.appendChild(meta);

    return () => {
      document.head.removeChild(meta);
    };
  }, [deepLinkPath]);

  const pageAttrs = {
    className: styles.page,
    ...(inviteId ? { 'data-invite-id': inviteId } : {}),
    ...(qrToken ? { 'data-qr-token': qrToken } : {}),
  };

  if (pageState.kind === 'loading') {
    return (
      <div {...pageAttrs}>
        <div className={styles.container}>
          <Spin size="large" tip={inviteCopyEn.loading} />
        </div>
      </div>
    );
  }

  if (pageState.kind === 'expired') {
    return (
      <div {...pageAttrs}>
        <div className={styles.container}>
          <Title className={styles.title}>{inviteCopyEn.title}</Title>
          <Paragraph className={styles.lead}>{inviteCopyEn.expired}</Paragraph>
        </div>
      </div>
    );
  }

  if (pageState.kind === 'not_found') {
    return (
      <div {...pageAttrs}>
        <div className={styles.container}>
          <Title className={styles.title}>{inviteCopyEn.title}</Title>
          <Paragraph className={styles.lead}>{inviteCopyEn.notFound}</Paragraph>
        </div>
      </div>
    );
  }

  const { invite, message } = pageState;
  const title = invite ? inviteCopyEn.titleWithInviter(invite.inviterName) : inviteCopyEn.title;
  const lead = invite
    ? inviteCopyEn.leadWithInviter(invite.inviterName)
    : message ?? (qrToken ? inviteCopyEn.qrLead : inviteCopyEn.lead);
  const showContinueOnWeb = Boolean(inviteId);
  const acceptPath = inviteId
    ? `/invite/${encodeURIComponent(inviteId)}/accept`
    : null;

  return (
    <div {...pageAttrs}>
      <div className={styles.container}>
        <div className={styles.icon}>
          <img
            src="/images/WandererWeatherlight.png"
            alt="WandererTrip"
            className={styles.iconImage}
          />
        </div>
        <Title className={styles.title}>{title}</Title>
        <Paragraph className={styles.lead}>{lead}</Paragraph>
        <Paragraph className={styles.hint}>{inviteCopyEn.notOnIos}</Paragraph>
        <div className={styles.ctaGroup}>
          <Button
            type="primary"
            size="large"
            icon={<AppleOutlined />}
            className={styles.cta}
            href={APP_STORE_URL}
          >
            {inviteCopyEn.cta}
          </Button>
          {showContinueOnWeb && acceptPath ? (
            <Link to={acceptPath}>
              <Button type="default" size="large" className={styles.secondaryCta}>
                {inviteCopyEn.continueOnWeb}
              </Button>
            </Link>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default Invite;
